/**
 * Thermal editor seeding — one author, unit-testable.
 *
 * D3 (Jun 2026): the editor seeded itself from `model.thermal_extras.picks`,
 * which on a full-thermal recommendation is the WIZARD'S step-7 selection and
 * not the recommended plant. On a 20 MW cement site (step 7 = 5 × 15 MW gas
 * engines, recommendation = OCGT 3 × 20 MW) editing only solar rebuilt "Your
 * configuration" on 75 MW of gas engines. When the recommendation IS the
 * thermal plant, the editor must open ON that plant.
 */

export const THERMAL_TECHS = ["gas_engine", "ocgt", "ccgt"];
export const DEFAULT_UNIT_MW_BY_TECH = { gas_engine: 1.5, ocgt: 5, ccgt: 30 };  // a06: the engine's CCGT block is 30 MW

/** The recommended thermal plant, read off the model. Null when absent. */
export const recommendedThermalPlant = (model) => {
  const tech = model?.thermal_tech;
  const count = Number(model?.gas_units || 0);
  const unitKw = Number(model?.gas_unit_size_kw || 0);
  if (!THERMAL_TECHS.includes(tech) || count <= 0 || unitKw <= 0) return null;
  return { tech, count, unit_mw: unitKw / 1000 };
};

/** True when a pick list is the same plant as `plant` (tech, count, unit size). */
export const isSamePlant = (picks, plant) => {
  const active = (picks || []).filter((p) => Number(p.count) > 0);
  if (!plant || active.length !== 1) return false;
  const p = active[0];
  return p.tech === plant.tech
         && Number(p.count) === Number(plant.count)
         && Math.round(Number(p.unit_mw) * 1000) === Math.round(Number(plant.unit_mw) * 1000);
};

/**
 * Seed rows for the thermal editor: one row per tech, count 0 when unused.
 * `isFullThermal` → seed from the RECOMMENDED plant; otherwise from the
 * wizard's picks.
 */
export const seedThermalPicks = ({ model, wizardPicks = [], isFullThermal = false }) => {
  const plant = isFullThermal ? recommendedThermalPlant(model) : null;
  const source = plant
    ? [{ tech: plant.tech, unit_mw: plant.unit_mw, count: plant.count, reasoning: "" }]
    : (wizardPicks || []).map((p) => ({
        tech: p.tech, unit_mw: p.unit_mw, count: p.count, reasoning: p.reasoning || "",
      }));
  const byTech = Object.fromEntries(source.map((p) => [p.tech, p]));
  return THERMAL_TECHS.map((t) =>
    byTech[t] || { tech: t, unit_mw: DEFAULT_UNIT_MW_BY_TECH[t], count: 0, reasoning: "" }
  );
};

/** Human label for a plant, e.g. "OCGT 3 × 20 MW". */
export const plantLabel = (plant, language = "en") => {
  if (!plant) return "";
  const names = {
    gas_engine: language === "fr" ? "Moteurs à gaz" : "Gas engines",
    ocgt: "OCGT",
    ccgt: "CCGT",
  };
  return `${names[plant.tech] || plant.tech} ${plant.count} × ${Number(plant.unit_mw)} MW`;
};
